/**
 * Helpers for "company-private" Explore categories.
 *
 * A "company-private root" category is the top-level Explore category that holds a
 * single gym's exclusive content. The backend stores these categories with
 * `identifier === gymId` so the URL slug and the gym identifier line up. This module
 * consolidates the heuristic so `CompanyContentViewScreen` and `CatchAllRoute` agree
 * on what a company root looks like, and route the viewer to `/company` (the index
 * route, which already renders the viewer's own company) instead of rendering an
 * inline branded screen at an `/explore-tab/:id` or `/company/:id` URL.
 *
 * EPIC-0009 — Slack thread p1777884466684699.
 */
import type { ExploreCategory } from './ExploreCategory';

type CompanyCategoryShape = Pick<ExploreCategory, 'identifier' | 'gymId'>;

/**
 * True when the category is the company-private root for some gym, detected by
 * `category.identifier === category.gymId` (with `gymId` truthy). Sub-categories of
 * a company (i.e. `gymId` set but `identifier !== gymId`) intentionally return
 * `false` — they render with the regular category chrome, not the company chrome.
 */
export const isCompanyRootCategory = (
  category: CompanyCategoryShape | null | undefined,
): boolean => !!category?.gymId && category.identifier === category.gymId;

/**
 * True only when the category is a company root AND the viewer belongs to that
 * gym. Use this to gate the `/company` redirect — cross-gym matches should
 * redirect to `/` instead so we never show one gym's branded chrome to a
 * customer of a different gym.
 *
 * Non-company-root categories return `false` from this helper because they
 * should not trigger the company redirect at all — fall through to the regular
 * category branch in that case.
 */
export const canViewerAccessCompany = (
  category: CompanyCategoryShape | null | undefined,
  viewerGymId: string | null | undefined,
): boolean =>
  isCompanyRootCategory(category) && !!viewerGymId && category!.gymId === viewerGymId;
